import { useEffect, useRef, useState } from "react";
import Portal from "./Portal";

interface PositionedPortalProps {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
}

/**
 * Positions portaled dropdown content relative to a trigger element.
 *
 * @param props - Trigger ref, open state, and dropdown content
 * @returns Portaled dropdown positioned under (or above) the trigger
 */
const PositionedPortal = ({
  children,
  triggerRef,
  isOpen,
}: PositionedPortalProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = rect.bottom + window.scrollY;
    let left = rect.left + window.scrollX;

    if (dropdownRef.current) {
      const dropdownHeight = dropdownRef.current.offsetHeight;
      if (rect.bottom + dropdownHeight > viewportHeight + window.scrollY) {
        top = rect.top + window.scrollY - dropdownHeight;
      }

      const dropdownWidth = dropdownRef.current.offsetWidth;
      if (rect.left + dropdownWidth > viewportWidth + window.scrollX) {
        left = rect.right + window.scrollX - dropdownWidth;
      }
    }

    setPosition({ top, left });
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        ref={dropdownRef}
        style={{
          position: "absolute",
          top: position.top,
          left: position.left,
          zIndex: 9999,
        }}
      >
        {children}
      </div>
    </Portal>
  );
};

export default PositionedPortal;
